import { atomWithHash } from '@/atoms/atomWithHash';
import { atom } from 'jotai';

export const isChooseNoteStructureModalOpenAtom = atomWithHash(
  'chooseNoteStructureModal',
  false
);

/**
 * Whether the next open of the note-structure picker should be dismissible
 * (Esc / outside click closes without choosing).
 *
 * The picker defaults to non-dismissible because the post-upload / pre-selection
 * auto-pop is intentionally a forced choice. Entry points that change the
 * template of an existing note — e.g. the V2 "Change template" row in
 * [[V2NoteTemplateRow]] — flip this to `true` alongside opening the picker so
 * the user can back out. [[ChooseNoteStructureModalMount]] resets it to `false`
 * when the picker closes so the next opener starts clean.
 */
export const isChooseNoteStructureModalDismissibleAtom = atom(false);

/**
 * True when the picker was reopened because the previously selected template is
 * no longer available (deleted, or no longer accessible to the user, before
 * note generation ran). Drives the inline warning banner in the picker footer
 * that replaces the old destructive toast.
 *
 * Set by the note-generation stream-response recovery when it reopens the
 * picker, and reset to `false` by [[ChooseNoteStructureModalMount]] when the
 * picker closes so it can't leak into the next open.
 */
export const chooseNoteStructureModalTemplateUnavailableAtom = atom(false);

/**
 * ID of the template rejected as unavailable (deleted or access denied) during
 * note generation.
 *
 * The picker excludes this template while showing the unavailable warning.
 * Set only by the feature-flagged recovery and reset on picker close.
 */
export const chooseNoteStructureModalUnavailableTemplateIdAtom = atom<string | null>(
  null
);

/**
 * Whether the user is left without a usable default template after generation
 * rejected the selected one: the rejected template IS the saved default, no
 * default is saved at all (deleting your own default clears
 * `default_template_id`), or the rejected template was itself a replacement for
 * a lost default. An Auto default (`DEFAULT_TEMPLATE_AUTO_MODE`) still counts
 * as usable.
 *
 * The replacement-template flow reads this before closing the picker and, only
 * after generation succeeds, offers to save the replacement as the new default.
 * Reset with the other recovery atoms when the picker closes.
 */
export const chooseNoteStructureModalUnavailableTemplateWasDefaultAtom = atom(false);

/**
 * True when the picker was reopened because note generation was refused for
 * lack of Pro Actions (the quota behind Pro templates and Ask Heidi commands).
 * Forces the Pro-styled out-of-Pro-Actions banner asking the user to select a
 * free template, which replaces the paywall modal on that path.
 *
 * Set by the note-generation stream-response recovery when it reopens the
 * picker, and reset to `false` by [[ChooseNoteStructureModalMount]] when the
 * picker closes so it can't leak into the next open.
 */
export const chooseNoteStructureModalOutOfProActionsAtom = atom(false);

/**
 * Display mode for the note-structure picker, used by the post-transcription
 * note-entry A/B/C experiment (GRO-3265 / GRO-3281):
 * - 'full': the standard picker with all chrome (control + every non-experiment
 *   trigger such as create-from-context or quota re-open).
 * - 'simple': stripped picker — no upgrade banner, create-template button,
 *   set-default switch, filters or pro chrome (Variant B / GRO-3265).
 * - 'auto_first': minimal Auto-first picker — Auto-only until the user types, no
 *   saved templates list (Variant C / GRO-3281).
 *
 * Only `_actionPostRecording` sets this to a non-'full' value, and only on the
 * transcription (recording-stop) path; it is reset to 'full' when the modal
 * closes so other open-triggers always get the standard picker.
 */
export type PostTranscriptionEntryMode = 'full' | 'simple' | 'auto_first';

export const postTranscriptionEntryModeAtom = atom<PostTranscriptionEntryMode>('full');

/**
 * True when the picker is running one of the simplified post-transcription
 * variants (`simple` / `auto_first`), which hide the sort/filter control
 * entirely.
 *
 * Derived once here so every "sort applies ⟺ sort control visible" gate reads
 * the same predicate. The persisted sort must only take effect in the full
 * picker; in the simplified variants TemplateFilter is hidden, so honouring a
 * stored order would leave an unresettable ordering (and in `auto_first` saved
 * templates are omitted, so a leaked sort would only affect the Auto /
 * Ask-Heidi section). Read by `templateViewDerivedAtom` and `useAICommandItems`
 * (sort gating), `index.tsx` (hides TemplateFilter), and `template-item.tsx`
 * (hides Pro / extra-actions chrome).
 */
export const isSimplifiedPickerAtom = atom(
  (get) => get(postTranscriptionEntryModeAtom) !== 'full'
);
